import { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Link, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MapUploadDialog({ open, onOpenChange, onMapLoaded }) {
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUrlSubmit = async () => {
    if (!url.trim()) return;
    try {
      const res = await axios.post(`${API}/maps/url`, { url: url.trim(), name: 'URL Map' });
      onMapLoaded(url.trim(), res.data.id);
      setUrl('');
      onOpenChange(false);
    } catch (e) {
      console.error('Failed to save map URL', e);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/maps/upload`, formData);
      onMapLoaded(res.data.file_id, res.data.id);
      onOpenChange(false);
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="map-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-emerald-400" style={{ fontFamily: 'Cinzel, serif' }}>
            Load Battle Map
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Provide an image URL or upload a file
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="url" className="mt-2">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="url" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-black" data-testid="tab-url">
              <Link className="w-4 h-4 mr-1.5" /> URL
            </TabsTrigger>
            <TabsTrigger value="upload" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-black" data-testid="tab-upload">
              <Upload className="w-4 h-4 mr-1.5" /> Upload
            </TabsTrigger>
          </TabsList>

          <TabsContent value="url" className="mt-4 space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs">Image URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/map.jpg"
                className="bg-zinc-900 border-zinc-700 mt-1 text-white placeholder:text-zinc-600"
                data-testid="map-url-input"
              />
            </div>
            <Button
              onClick={handleUrlSubmit}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-black font-semibold"
              disabled={!url.trim()}
              data-testid="map-url-submit"
            >
              Load from URL
            </Button>
          </TabsContent>

          <TabsContent value="upload" className="mt-4 space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="map-file-input"
            />
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500/50 transition-colors"
              data-testid="map-upload-dropzone"
            >
              {uploading ? (
                <Loader2 className="w-8 h-8 mx-auto text-emerald-500 animate-spin" />
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                  <p className="text-sm text-zinc-400">Click to select an image</p>
                  <p className="text-xs text-zinc-600 mt-1">PNG, JPG, WebP supported</p>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
